(function () {
  // brand red #ab0000 for the wireframe (front and back lines)
  const RED = [171, 0, 0], BRIGHT = [171, 0, 0], WHITE = [245, 245, 245];
  const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
  const lerp = (a, b, t) => a + (b - a) * t;
  const imgCache = {};
  function loadImg(src) {
    if (!imgCache[src]) { const i = new Image(); i.src = src; imgCache[src] = i; }
    return imgCache[src];
  }

  function fib(n) {
    const pts = [], g = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < n; i++) {
      const y = 1 - (i / (n - 1)) * 2, r = Math.sqrt(1 - y * y), t = g * i;
      pts.push([Math.cos(t) * r, y, Math.sin(t) * r, Math.random()]);
    }
    return pts;
  }

  function create(canvas, opts) {
    const o = Object.assign({ style: 'wire', logo: 'core', src: 'assets/eye-logo.png', speed: 0.12, hover: 1, radius: 0.36, cy: 0.5 }, opts);
    const ctx = canvas.getContext('2d');
    const img = loadImg(o.src);
    const backImg = o.backSrc ? loadImg(o.backSrc) : null;
    const dots = fib(o.style === 'dots' ? 1600 : 0);
    const NZ = 256, noise = [0, 1, 2].map(() => {
      const c = document.createElement('canvas'); c.width = c.height = NZ;
      const x = c.getContext('2d'), id = x.createImageData(NZ, NZ);
      for (let i = 0; i < id.data.length; i += 4) { const v = Math.random() * 255; id.data[i] = id.data[i + 1] = id.data[i + 2] = v; id.data[i + 3] = Math.random() < 0.5 ? Math.random() * 255 : 0; }
      x.putImageData(id, 0, 0); return c;
    });
    let W = 0, H = 0, dpr = 1, raf = 0, last = performance.now();
    let rot = 0.6, tilt = -0.28, yaw = 0, hov = 0, isHover = false, mx = 0, my = 0, glitchT = 0, time = 0;
    const host = canvas.parentElement;

    function resize() {
      // speed: the pixel look hides resolution, so draw it at 1x; smooth mode tops out at 1.5x
      dpr = o.pixel > 1 ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
    }
    const ro = new ResizeObserver(resize); ro.observe(canvas); resize();

    // bleed: the canvas extends this fraction past its box on each side so the glow has room
    const geo = () => { const R = Math.min(W, H) / (1 + 2 * (o.bleed || 0)) * o.radius; return { R, cx: W / 2, cy: H * o.cy }; };
    function onMove(e) {
      const b = canvas.getBoundingClientRect(); mx = e.clientX - b.left; my = e.clientY - b.top;
      const { R, cx, cy } = geo();
      const inside = Math.hypot(mx - cx, my - cy) < R * 1.12;
      if (inside && !isHover) glitchT = 0.35;
      isHover = inside; canvas.style.cursor = inside ? 'pointer' : 'default';
    }
    function onLeave(e) { if (e && e.pointerType === 'touch') return; isHover = false; canvas.style.cursor = 'default'; }
    // touch: a tap on the globe toggles the hover state; a tap elsewhere clears it
    function onDown(e) { if (e.pointerType !== 'touch') return; onMove(e); }
    function onDocDown(e) { if (e.pointerType === 'touch' && !host.contains(e.target)) isHover = false; }
    host.addEventListener('pointermove', onMove); host.addEventListener('pointerleave', onLeave);
    host.addEventListener('pointerdown', onDown); document.addEventListener('pointerdown', onDocDown);

    // honour reduced-motion: slow the spin and skip flicker/glitch
    const rm = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    const calm = () => !!(rm && rm.matches);

    // only animate while the globe is on screen and the tab is visible
    let onScreen = true;
    const io = new IntersectionObserver((es) => { onScreen = es[0].isIntersecting; wake(); });
    io.observe(canvas);
    function onVis() { wake(); }
    document.addEventListener('visibilitychange', onVis);
    function wake() {
      if (onScreen && !document.hidden && !raf) { last = performance.now(); raf = requestAnimationFrame(frame); }
    }

    // rotate around Y by a, then X by t
    function proj(x, y, z, a, t) {
      const ca = Math.cos(a), sa = Math.sin(a);
      let X = x * ca + z * sa, Z = -x * sa + z * ca, Y = y;
      const ct = Math.cos(t), st = Math.sin(t);
      const Y2 = Y * ct - Z * st, Z2 = Y * st + Z * ct;
      return [X, Y2, Z2];
    }

    function drawWire(R, cx, cy, a, t, k) {
      const back = new Path2D(), front = new Path2D();
      const seg = (pts) => {
        let prevF = null;
        for (let i = 0; i < pts.length; i++) {
          const [x, y, z] = proj(pts[i][0], pts[i][1], pts[i][2], a, t);
          const sx = cx + x * R, sy = cy - y * R, f = z >= 0;
          const p = f ? front : back;
          if (i === 0 || f !== prevF) p.moveTo(sx, sy); else p.lineTo(sx, sy);
          prevF = f;
        }
      };
      const N = 72;
      for (let lat = -75; lat <= 75; lat += 15) {
        const phi = lat * Math.PI / 180, r = Math.cos(phi), y = Math.sin(phi), pts = [];
        for (let i = 0; i <= N; i++) { const th = i / N * Math.PI * 2; pts.push([Math.cos(th) * r, y, Math.sin(th) * r]); }
        seg(pts);
      }
      for (let lon = 0; lon < 180; lon += 15) {
        const th = lon * Math.PI / 180, pts = [];
        for (let i = 0; i <= N; i++) { const phi = i / N * Math.PI * 2; pts.push([Math.cos(phi) * Math.cos(th), Math.sin(phi), Math.cos(phi) * Math.sin(th)]); }
        seg(pts);
      }
      ctx.lineWidth = 1;
      ctx.strokeStyle = rgba(RED, 0.22 + 0.1 * k); ctx.stroke(back);
      ctx.lineWidth = 1.4 + 0.4 * k;
      ctx.strokeStyle = rgba(BRIGHT, 0.55 + 0.35 * k); ctx.stroke(front);
      // chromatic white ghost
      ctx.save(); ctx.translate(1.2 + 2 * k, 0); ctx.lineWidth = 0.6;
      ctx.strokeStyle = rgba(WHITE, 0.08 + 0.14 * k); ctx.stroke(front); ctx.restore();
    }

    function drawDots(R, cx, cy, a, t, k) {
      for (const d of dots) {
        const [x, y, z] = proj(d[0], d[1], d[2], a, t);
        let sx = cx + x * R, sy = cy - y * R;
        let boost = 0;
        if (k > 0.01) {
          const dx = sx - mx, dy = sy - my, dist = Math.hypot(dx, dy), rad = R * 0.45;
          if (dist < rad && z > 0) {
            const f = (1 - dist / rad); boost = f * k;
            sx += (dx / (dist || 1)) * f * f * R * 0.12 * k; sy += (dy / (dist || 1)) * f * f * R * 0.12 * k;
          }
        }
        const front = z >= 0;
        const s = front ? (0.8 + z * 1.4 + boost * 1.6) : 0.7;
        const tw = 0.75 + 0.25 * Math.sin(time * 2 + d[3] * 20);
        const col = d[3] > 0.82 ? WHITE : (front ? BRIGHT : RED);
        const al = front ? (0.35 + z * 0.55) * tw + boost * 0.4 : 0.13 + 0.05 * k;
        ctx.fillStyle = rgba(col, Math.min(al, 1));
        ctx.fillRect(sx - s / 2, sy - s / 2, s, s);
      }
    }

    function drawDecal(R, cx, cy, a, t, k) {
      if (!img.complete || !img.naturalWidth) return;
      const c = proj(0, 0, 1, a, t), e = proj(1, 0, 0, a, t), n = proj(0, 1, 0, a, t);
      const s = R * 0.5 * (1 + 0.06 * k), z = c[2];
      const al = z >= 0 ? 0.55 + 0.45 * z : 0.14 * (1 + z);
      ctx.save();
      ctx.globalAlpha = al;
      ctx.setTransform(dpr * e[0] * s, -dpr * e[1] * s, -dpr * n[0] * s, dpr * n[1] * s, dpr * (cx + c[0] * R * 1.01), dpr * (cy - c[1] * R * 1.01));
      if (z >= 0) { ctx.shadowColor = rgba(BRIGHT, 0.9); ctx.shadowBlur = 18 + 20 * k; }
      ctx.drawImage(img, -1, -1, 2, 2);
      ctx.restore();
    }

    // logo printed on the far side of the sphere; faces the viewer every half turn
    function drawBack(R, cx, cy, a, t, k) {
      if (!backImg || !backImg.complete || !backImg.naturalWidth) return;
      if (o.backFixed) {
        const s = R * (o.backSize || 0.62);
        ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, R * 0.98, 0, Math.PI * 2); ctx.clip();
        ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 0.22 + 0.12 * k;
        ctx.shadowColor = rgba(WHITE, 0.5); ctx.shadowBlur = 10 + 14 * k;
        ctx.drawImage(backImg, cx - s, cy - s, s * 2, s * 2);
        ctx.restore(); return;
      }
      const c = proj(0, 0, -1, a, t), e = proj(-1, 0, 0, a, t), n = proj(0, 1, 0, a, t);
      const s = R * (o.backSize || 0.62), z = c[2];
      const al = z >= 0 ? 0.35 + 0.65 * z : 0.12 * (1 + z);
      ctx.save(); ctx.globalAlpha = al * (0.85 + 0.15 * k);
      ctx.setTransform(dpr * e[0] * s, -dpr * e[1] * s, -dpr * n[0] * s, dpr * n[1] * s, dpr * (cx + c[0] * R), dpr * (cy - c[1] * R));
      if (z >= 0) { ctx.shadowColor = rgba(WHITE, 0.7); ctx.shadowBlur = 10 + 14 * k; }
      ctx.drawImage(backImg, -1, -1, 2, 2);
      ctx.restore();
    }

    function drawCore(R, cx, cy, k) {
      if (!img.complete || !img.naturalWidth) return;
      const pulse = 0.5 + 0.5 * Math.sin(time * 1.4);
      const s = R * (0.5 + 0.05 * k + 0.012 * pulse);
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, s * 1.6);
      g.addColorStop(0, rgba(BRIGHT, 0.28 + 0.25 * k)); g.addColorStop(1, rgba(BRIGHT, 0));
      ctx.fillStyle = g; ctx.fillRect(cx - s * 2, cy - s * 2, s * 4, s * 4);
      ctx.save(); ctx.globalCompositeOperation = 'source-over'; ctx.shadowColor = rgba(BRIGHT, 1); ctx.shadowBlur = 14 + 26 * k;
      ctx.globalAlpha = 1;
      // slight breathing squash so it reads as a projection, not a sticker
      ctx.translate(cx, cy); ctx.scale(1, 1 - 0.015 * pulse);
      ctx.drawImage(img, -s, -s, s * 2, s * 2); ctx.restore();
    }

    function drawRings(R, cx, cy, k) {
      for (let i = 0; i < 2; i++) {
        const rr = R * (1.22 + i * 0.16) * (1 + 0.05 * k), tiltR = 0.22 + i * 0.1, sp = time * (0.4 - i * 0.15) * (1 + k);
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(i ? 0.18 : -0.12);
        ctx.beginPath(); ctx.ellipse(0, 0, rr, rr * tiltR, 0, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(WHITE, 0.12 + 0.12 * k); ctx.lineWidth = 1; ctx.stroke();
        for (let j = 0; j < 3; j++) {
          const th = sp + j * Math.PI * 2 / 3, px = Math.cos(th) * rr, py = Math.sin(th) * rr * tiltR;
          ctx.fillStyle = rgba(j ? WHITE : BRIGHT, Math.sin(th) > 0 ? 0.95 : 0.3);
          ctx.fillRect(px - 2, py - 2, 4, 4);
        }
        ctx.restore();
      }
    }

    function drawBase(R, cx, cy, k) {
      const by = cy + R * 1.38, bw = R * 0.95;
      const beam = ctx.createLinearGradient(0, by, 0, cy - R);
      beam.addColorStop(0, rgba(BRIGHT, 0.32 + 0.2 * k)); beam.addColorStop(1, rgba(BRIGHT, 0));
      ctx.fillStyle = beam; ctx.beginPath();
      ctx.moveTo(cx - bw * 0.55, by); ctx.lineTo(cx - R * 1.05, cy - R * 0.2); ctx.lineTo(cx + R * 1.05, cy - R * 0.2); ctx.lineTo(cx + bw * 0.55, by); ctx.closePath(); ctx.fill();
      for (let i = 0; i < 3; i++) {
        ctx.beginPath(); ctx.ellipse(cx, by + i * 7, bw * (0.55 + i * 0.12), bw * (0.09 + i * 0.02), 0, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(i ? RED : BRIGHT, i ? 0.35 : 0.8 + 0.2 * k); ctx.lineWidth = i ? 1 : 1.6; ctx.stroke();
      }
    }

    function scan(R, cx, cy, k) {
      ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, R * 1.02, 0, Math.PI * 2); ctx.clip();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(9,8,10,0.35)';
      for (let y = cy - R; y < cy + R; y += 3) ctx.fillRect(cx - R, y, R * 2, 1);
      const band = cy - R + ((time * 0.25) % 1) * R * 2.4 - R * 0.2;
      const g = ctx.createLinearGradient(0, band - 30, 0, band + 30);
      g.addColorStop(0, rgba(WHITE, 0)); g.addColorStop(0.5, rgba(WHITE, 0.07 + 0.08 * k)); g.addColorStop(1, rgba(WHITE, 0));
      ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = g; ctx.fillRect(cx - R, band - 30, R * 2, 60);
      ctx.restore();
    }

    function grain(R, cx, cy, k) {
      const amt = o.grain;
      const pat = ctx.createPattern(noise[Math.floor(time * 24) % 3], 'repeat');
      const ox = Math.random() * NZ, oy = Math.random() * NZ;
      ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.clip();
      ctx.translate(ox, oy); ctx.fillStyle = pat;
      // erode the hologram so lines read screen-printed, not vector-clean
      ctx.globalCompositeOperation = 'destination-out'; ctx.globalAlpha = 0.55 * amt;
      ctx.fillRect(-ox, -oy, W, H);
      // sparkle specks of light inside the globe
      ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = (0.1 + 0.06 * k) * amt;
      ctx.fillRect(-ox, -oy, W, H);
      ctx.restore();
    }

    function glitch(R, cx, cy) {
      const n = 5;
      for (let i = 0; i < n; i++) {
        const y = (cy - R + Math.random() * R * 2) * dpr, h = (4 + Math.random() * 18) * dpr, off = (Math.random() - 0.5) * 24 * dpr;
        ctx.drawImage(canvas, 0, y, canvas.width, h, off, y, canvas.width, h);
      }
    }

    // Pixel look (matches the pixelated photoshoot): shrink the finished frame to a coarse grid,
    // smear each row sideways like a scan streak, then blow it back up with hard pixel edges.
    // o.pixel = block size in CSS px (0/1 = off); o.streak = smear strength (0 - 1.5).
    const small = document.createElement('canvas'), sctx = small.getContext('2d');
    let rowLen = [];
    function pixelate(k) {
      const px = o.pixel * dpr;
      const sw = Math.max(1, Math.ceil(canvas.width / px)), sh = Math.max(1, Math.ceil(canvas.height / px));
      if (small.width !== sw || small.height !== sh) { small.width = sw; small.height = sh; rowLen = []; }
      sctx.globalCompositeOperation = 'copy'; sctx.globalAlpha = 1; sctx.imageSmoothingEnabled = true;
      sctx.drawImage(canvas, 0, 0, sw, sh);
      const st = o.streak == null ? 1 : o.streak;
      if (st > 0) {
        // each row keeps its own streak length, re-rolled a few rows at a time so it shimmers
        for (let y = 0; y < sh; y++) if (rowLen[y] == null || Math.random() < 0.04) {
          // signed: streaks run left or right, like the photo's scan smear
          rowLen[y] = Math.random() < 0.55 ? (Math.random() < 0.5 ? -1 : 1) * Math.pow(Math.random(), 1.6) * sw * 0.22 : 0;
        }
        sctx.globalCompositeOperation = 'lighter';
        for (let y = 0; y < sh; y++) {
          const L = rowLen[y] * st * (1 + 0.8 * k);
          if (Math.abs(L) < 1) continue;
          // one smear pass per row (was three) keeps the look at a third of the cost
          sctx.globalAlpha = 0.42;
          sctx.drawImage(small, 0, y, sw, 1, L * 0.6, y, sw, 1);
        }
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'copy'; ctx.globalAlpha = 1;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(small, 0, 0, sw * px, sh * px);
      // (no extra brighten pass: it pushed the reds well past the brand #ab0000)
      ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
      ctx.imageSmoothingEnabled = true;
    }

    function frame(now) {
      // speed: cap redraws (default 30 fps) instead of drawing every screen refresh
      if (now - last < 1000 / (o.fps || 30) - 2) {
        raf = onScreen && !document.hidden ? requestAnimationFrame(frame) : 0;
        return;
      }
      const dt = Math.max(0, Math.min((now - last) / 1000, 0.05)); last = now; time += dt;
      hov = lerp(hov, isHover ? 1 : 0, 1 - Math.pow(0.001, dt));
      const k = hov * o.hover;
      const { R, cx, cy } = geo();
      if (o.logo === 'decal' && isHover) {
        // ease the globe round so the eye faces the viewer
        const target = Math.round(rot / (Math.PI * 2)) * Math.PI * 2;
        rot = lerp(rot, target, 1 - Math.pow(0.08, dt));
      } else rot += dt * o.speed * (calm() ? 0.35 : 1) * (1 + 2.5 * k);
      const ty = isHover ? ((my - cy) / R) * -0.25 - 0.18 : -0.28;
      const tx = isHover ? ((mx - cx) / R) * 0.25 : 0;
      tilt = lerp(tilt, ty, 1 - Math.pow(0.05, dt)); yaw = lerp(yaw, tx, 1 - Math.pow(0.05, dt));
      const a = rot + yaw;
      const flick = !calm() && Math.random() < 0.02 ? 0.75 : 1;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.globalAlpha = flick;
      // halo
      const hg = ctx.createRadialGradient(cx, cy, R * 0.6, cx, cy, R * 1.5);
      hg.addColorStop(0, rgba(RED, 0.16 + 0.18 * k)); hg.addColorStop(1, rgba(RED, 0));
      ctx.fillStyle = hg; ctx.fillRect(0, 0, W, H);
      if (o.style === 'projector') drawBase(R, cx, cy, k);
      ctx.globalCompositeOperation = 'lighter';
      if (backImg) drawBack(R, cx, cy, a, tilt, k);
      if (o.logo === 'core') drawCore(R, cx, cy, k);
      if (o.style === 'dots') drawDots(R, cx, cy, a, tilt, k); else drawWire(R, cx, cy, a, tilt, k);
      // rim
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(BRIGHT, 0.5 + 0.4 * k); ctx.lineWidth = 1.5; ctx.stroke();
      if (o.logo === 'decal') drawDecal(R, cx, cy, a, tilt, k);
      if (o.style === 'projector') drawRings(R, cx, cy, k);
      ctx.globalCompositeOperation = 'source-over';
      if (o.style !== 'dots') scan(R, cx, cy, k);
      if (o.grain) grain(R, cx, cy, k);
      ctx.globalAlpha = 1;
      if (glitchT > 0) { glitchT -= dt; if (!calm()) { ctx.setTransform(1, 0, 0, 1, 0, 0); glitch(R, cx, cy); } }
      if (o.pixel > 1) pixelate(k);
      raf = onScreen && !document.hidden ? requestAnimationFrame(frame) : 0;
    }
    raf = requestAnimationFrame(frame);

    return {
      set(p) { Object.assign(o, p); },
      destroy() {
        cancelAnimationFrame(raf); raf = 0; ro.disconnect(); io.disconnect();
        host.removeEventListener('pointermove', onMove); host.removeEventListener('pointerleave', onLeave);
        host.removeEventListener('pointerdown', onDown); document.removeEventListener('pointerdown', onDocDown);
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }
  window.MXGlobe = { create };
})();
